import { Divider } from '@heroui/react';
import React from 'react';
import {
  Controller,
  Control,
  FieldErrors,
  UseFormWatch,
} from 'react-hook-form';

import { ChocoCheckbox } from '../../../../../components/FormComponents';
import { SupportLogin } from '../../SupportLogin';
import { UserFormData } from '../hooks/useUserFormValidation';

interface UserTypeSectionProps {
  control: Control<UserFormData>;
  errors: FieldErrors<UserFormData>;
  watch: UseFormWatch<UserFormData>;
  isEditMode?: boolean;
  disabled?: boolean;
}

export const UserTypeSection: React.FC<UserTypeSectionProps> = ({
  control,
  errors,
  watch,
  isEditMode = false,
  disabled = false,
}) => {
  const watchSupplier = watch('supplier');

  return (
    <>
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground">User Type</h3>

        {isEditMode && (
          <div className="p-3 bg-default-100 border border-default-200 rounded-lg">
            <p className="text-sm text-default-700">
              <strong>Note:</strong> User type cannot be changed after creation.
            </p>
          </div>
        )}

        <div className="flex flex-col gap-5">
          <Controller
            name="isTest"
            control={control}
            render={({ field }) => (
              <ChocoCheckbox
                isSelected={field.value}
                onValueChange={field.onChange}
                isDisabled={isEditMode || disabled}
                size="sm"
                label="Test User"
                description="Mark this user as a test account"
              />
            )}
          />

          <Controller
            name="supplier"
            control={control}
            render={({ field }) => (
              <ChocoCheckbox
                isSelected={field.value}
                onValueChange={field.onChange}
                isDisabled={isEditMode || disabled}
                size="sm"
                label="Distributor"
                description="This user represents a distributor"
              />
            )}
          />

          {/* Support Login - only show in edit mode and when user has phone number */}
          {isEditMode && watch('phone') && (
            <Controller
              name="supportLogin"
              control={control}
              render={({ field }) => (
                <SupportLogin
                  isEnabled={field.value || false}
                  onToggle={field.onChange}
                  phoneNumber={watch('phone') || ''}
                  userName={watch('name') || ''}
                  disabled={disabled}
                  isPhoneLoginOnly={watch('loginMethod') !== 'phone'}
                />
              )}
            />
          )}
        </div>
      </div>

      {!watchSupplier && (
        <div className="p-4 bg-warning-50 border border-warning-200 rounded-lg">
          <p className="text-sm text-warning-800">
            <strong>Note:</strong> Users must be either test users,
            distributors, or linked to buyers to be valid.
          </p>
        </div>
      )}

      <Divider />
    </>
  );
};
